import { useEffect, useRef, useCallback } from 'react';
import { App } from '@capacitor/app';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

/**
 * Hook to handle Android hardware back button
 * 
 * Features:
 * - Closes modals before navigating
 * - Navigates back through history
 * - Shows logout confirmation on root pages
 * - Properly exits app after logout
 * 
 * @param {Object} options
 * @param {boolean} options.modalOpen - Whether a modal is currently open
 * @param {Function} options.closeModal - Function to close the modal
 * @param {Function} options.onLogoutRequest - Callback when user wants to exit from root
 * @param {string[]} options.exitRoutes - Routes where back = exit app (default: ['/', '/dashboard'])
 */
export const useAndroidBackButton = ({
    modalOpen = false,
    closeModal = null,
    onLogoutRequest = null,
    exitRoutes = ['/', '/dashboard', '/nominee/dashboard']
} = {}) => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Only set up listener in Capacitor environment
        const isCapacitor = window.Capacitor?.isNativePlatform?.() ?? false;
        if (!isCapacitor) return;

        const handleBackButton = App.addListener('backButton', ({ canGoBack }) => {
            // Priority 1: Close modal if open
            if (modalOpen && closeModal) {
                closeModal();
                return;
            }

            // Priority 2: Check if we're on an exit route
            const isExitRoute = exitRoutes.some(route =>
                location.pathname === route || location.pathname === route + '/'
            );

            if (isExitRoute) {
                if (onLogoutRequest) {
                    onLogoutRequest();
                } else {
                    // Fallback to double-press exit if no logout handler provided
                    App.exitApp();
                }
                return;
            }

            // Priority 3: Navigate back in history
            if (canGoBack || window.history.length > 1) {
                window.history.back();
            } else {
                // Fallback: go to dashboard
                navigate('/dashboard');
            }
        });

        return () => {
            handleBackButton.remove();
        };
    }, [modalOpen, closeModal, location.pathname, navigate, exitRoutes, onLogoutRequest]);
};

/**
 * Global back button handler component
 * Place this in your App.jsx to enable basic back navigation
 */
export const AndroidBackHandler = ({ onLogoutRequest }) => {
    useAndroidBackButton({ onLogoutRequest });
    return null;
};

